import {
  inject,
  Injectable,
  Injector,
  reflectComponentType,
  TemplateRef,
  DOCUMENT
} from '@angular/core';

import { Directionality } from '@angular/cdk/bidi';
import { OverlayConfig, Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { TranslateService } from '@ngx-translate/core';

import { BehaviorSubject, of, Subject, take } from 'rxjs';

import { ScreenReaderService } from '../../services/screen-reader.service';
import { DataAnalyticsService } from '../../services/data-analytics/data-analytics.service';
import { PlatformService } from '../../services/platform.service';
import { MODAL_CONFIG, MODAL_CONTAINER, MODAL_DATA } from './modal.domain';
import { ModalOptions } from './modal-options';
import { ModalRef } from './modal-ref';
import { MODAL_SCROLL_STRATEGY_FACTORY } from './modal-scroll-strategy-factory';
import type { DataAnalyticsModalData } from '../../services/data-analytics/data-analytics.domain';
import type { ModalCloseReason, ModalContainer, ModalData } from './modal.domain';
import type { ComponentType, OverlayRef, ScrollStrategy } from '@angular/cdk/overlay';
import type {
  ComponentRef,
  StaticProvider
} from '@angular/core';

/**
 * A service to open modal overlays.
 *
 * Inspired by:
 * https://github.com/angular/material2/blob/master/src/lib/dialog/dialog.ts
 * https://github.com/angular/material2/commits/master/src/cdk-experimental/dialog/dialog.ts
 * https://github.com/ng-bootstrap/ng-bootstrap/blob/master/src/modal/modal.ts
 */
@Injectable({
  providedIn: 'root'
})
export class ModalService {

  readonly areModalsOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** Return stream that emits when a modal is opened. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get afterOpen(): Subject<ModalRef<any>> {
    return this.parent !== null && this.parent !== undefined ? this.parent.afterOpen : this._afterOpen;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly _afterOpen = new Subject<ModalRef<any>>();

  /** Return a collection of open modal modals. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get openModals(): ReadonlyArray<ModalRef<any>> {
    return this.parent !== null && this.parent !== undefined ? this.parent.openModals : this._openModals;
  }

  ariaHiddenElements: Map<Element, string>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly _openModals: Array<ModalRef<any>> = [];

  private elementsFocusedBefore: Array<HTMLElement> = [];

  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly scrollStrategyFactory: (modalOptions: ModalOptions) => ScrollStrategy = inject(MODAL_SCROLL_STRATEGY_FACTORY);
  private readonly modalContainer = inject(MODAL_CONTAINER);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly dataAnalyticsService = inject(DataAnalyticsService);
  private readonly document = inject(DOCUMENT);
  private readonly platformService = inject(PlatformService);
  private readonly parent: ModalService | null = inject(ModalService, { optional: true, skipSelf: true });
  private readonly translateService = inject(TranslateService);

  constructor() {
    /**
     * This code is used to carry forward the ariaHiddenElements in the event of a modal spawning another modal (such as watchlist opening buy/sell)
     */
    if (this.parent !== null && this.parent !== undefined) {
      this.ariaHiddenElements = this.parent.ariaHiddenElements;
    } else {
      this.ariaHiddenElements = new Map<Element, string>();
    }
  }

  /**
   * even though we could support multiple modals opened at the same time this functionality is not used
   *
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTopModal(): ModalRef<any> | undefined {
    if (this.openModals.length > 0) {
      return this.openModals[this.openModals.length - 1];
    }
  }

  /** Finds an open modal by its id. */
  getModalById<T>(id: string): ModalRef<T> | undefined {
    return this.openModals.find((modalRef: ModalRef<T>): boolean => modalRef.id === id);
  }

  /**
   * Open a template or component in a modal.
   *
   * @param content Modal content. Can be component or template
   * @param options
   * @param modalData
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  open<T, R = any>(content: TemplateRef<T> | ComponentType<T>,
                   options?: ModalOptions,
                   modalData?: ModalData): ModalRef<T, R> {
    options = this.applyConfigDefaults(options);

    if (options.id !== undefined && this.getModalById(options.id) !== undefined) {
      throw Error(`Modal with id "${options.id}" exists already. The id must be unique.`);
    }

    const overlayRef: OverlayRef = this.createOverlay(options);
    const modalContainer: ModalContainer = this.attachModalContainer(overlayRef, options);

    // Create a reference to the modal we're creating in order to give the user a handle to modify and close it.
    const modalRef: ModalRef<T, R> = new ModalRef<T, R>(overlayRef, modalContainer, options.id, options.closeAsync);

    if (content instanceof TemplateRef) {
      // attach new template portal as the content of the already created modal container
      modalContainer.attachTemplatePortal(new TemplatePortal<T>(content, null, { $implicit: modalRef } as T));
    } else {
      // attach new component portal to the already created modal container
      const injector = this.createInjector<T>(options, modalRef, modalContainer, modalData);
      const contentRef = modalContainer.attachComponentPortal(new ComponentPortal(content, null, injector));

      modalRef.componentInstance = contentRef.instance;

      if (modalData !== undefined) {
        this.setInputBindings(contentRef, modalData);
      }
    }
    modalRef
      .updateSize({ width: options.width, height: options.height })
      .updatePosition(options.position);
    if (this.openModals.length === 0) {
      this.toggleNonModalContentScreenReaderVisibility(false);
    }
    this.registerModalRef(modalRef);

    this.dataAnalyticsService.registerModalEvents(modalRef, modalData as DataAnalyticsModalData);

    return modalRef;
  }

  /**
   * Closes all currently open modals.
   *
   * NOTE: openModals property is not updated after close until the RxJs subscription
   * runs on the next micro task. see registerModalRef
   */
  closeAll(reason?: ModalCloseReason): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.openModals.forEach((ref: ModalRef<any>): void => ref.close(reason));
  }

  /**
   * This method is used to get around issues in JAWS screen reader which cannot handle multiple elements with aria-modal
   * attribute. Before opening any modal overlay currently open modal have to have aria-modal and dialog attributes removed
   *
   * @param visible
   */
  toggleScreenReaderVisibility(visible: boolean): void {
    const containerElement = this.overlayContainer.getContainerElement();
    if (visible) {
      containerElement.removeAttribute('aria-hidden');
    } else {
      containerElement.setAttribute('aria-hidden', 'true');
    }
    this.openModals.forEach((modalRef: ModalRef<ModalContainer>): void => {
      modalRef.toggleScreenReaderVisibility(visible);
    });
  }

  autoSuggestPositionAdjustment(open: boolean = true): void {
    if (this.platformService.isIOS()) {
      const modalContainer = this.document.getElementsByClassName('td-wb-modal-container')?.item(0);
      if (modalContainer) {
        if (open) {
          modalContainer.classList.add('td-wb-modal-container--auto-suggest');
        } else {
          modalContainer.classList.remove('td-wb-modal-container--auto-suggest');
        }
      }
    }
  }

  private attachModalContainer(overlayRef: OverlayRef, options: ModalOptions): ModalContainer {
    const userInjector: Injector | undefined = this.getPreconfiguredInjector(options);

    const injector: Injector = Injector.create({
      providers: [{ provide: ModalOptions, useValue: options }],
      parent: userInjector ?? this.injector
    });

    const containerPortal = new ComponentPortal<ModalContainer>(
      this.modalContainer, options.viewContainerRef, injector);
    const containerRef: ComponentRef<ModalContainer> = overlayRef.attach(containerPortal);
    containerRef.instance.options = options;
    return containerRef.instance;
  }

  /**
   * Creates a custom injector to be used inside the modal which
   * provides access to ModalWindowComponent,
   * modal data - custom data which ca be passed to the
   * modal e.g. when modal is open  using router url it might contain resolve data
   * ModalRef object which can be used to close the modal
   *
   * NOTE: The container (ModalWindowComponent) injected in the portal and the modal's content are created out of
   * the same ViewContainerRef and as such, are siblings for injector purposes. To allow the expected hierarchy,
   * the ModalWindowComponent is explicitly added to the injection tokens.
   */
  private createInjector<T>(options: ModalOptions,
                            modalRef: ModalRef<T>,
                            modalContainer: ModalContainer,
                            modalData?: ModalData): Injector {
    const userInjector: Injector | undefined = this.getPreconfiguredInjector(options);

    const injectionTokens: Array<StaticProvider> = [];
    injectionTokens.push({ provide: ModalRef, useValue: modalRef });
    injectionTokens.push({ provide: MODAL_CONTAINER, useValue: modalContainer });

    if (modalData !== undefined) {
      injectionTokens.push({ provide: MODAL_DATA, useValue: modalData });
    }

    if (options.direction !== undefined
      && (userInjector?.get<Directionality | null>(Directionality, null) == null)) {
      injectionTokens.push({ provide: Directionality, useValue: { value: options.direction, change: of() } });
    }
    return Injector.create({
      providers: injectionTokens,
      parent: userInjector ?? this.injector
    });
  }

  /**
   * Sets components inputs from modal data
   *
   * @param contentRef
   * @param modalData
   */
  private setInputBindings<T>(contentRef: ComponentRef<T>, modalData: ModalData): void {
    const componentInputs = reflectComponentType(contentRef.componentType)?.inputs;
    if (componentInputs !== undefined) {
      Object.keys(modalData).forEach((key: string): void => {
        const matchingInput = componentInputs.find(
          (input: { propName: string; templateName: string }): boolean => input.templateName === key);
        if (matchingInput !== undefined) {
          contentRef.setInput(matchingInput.templateName, modalData[matchingInput.templateName]);
        }
      });
    }
  }

  /**
   * Expands the provided configuration object with the default values.
   */
  private applyConfigDefaults(options?: ModalOptions): ModalOptions {
    const modalConfig = this.injector.get(MODAL_CONFIG);
    modalConfig.ariaLabel = this.translateService.instant(modalConfig.ariaLabel);
    return { ...modalConfig, ...options };
  }

  /**
   * Creates the overlay into which the modal will be loaded.
   *
   * @param config The modal configuration.
   * @returns A promise resolving to the OverlayRef for the created overlay.
   */
  private createOverlay(config: ModalOptions): OverlayRef {
    const overlayConfig = this.getOverlayConfig(config);
    return this.overlay.create(overlayConfig);
  }

  /**
   * Creates an overlay config from a modal options.
   *
   * @param modalOptions The modal configuration.
   * @returns The overlay configuration.
   */
  private getOverlayConfig(modalOptions: ModalOptions): OverlayConfig {
    const positionStrategy = this.overlay.position().global();
    const overlayConfig = { ...modalOptions, ...{ positionStrategy } } as OverlayConfig;

    overlayConfig.scrollStrategy ??= this.scrollStrategyFactory(modalOptions);
    return new OverlayConfig(overlayConfig);
  }

  private getPreconfiguredInjector(options: ModalOptions): Injector | undefined {
    return options?.viewContainerRef?.injector ?? undefined;
  }

  /**
   * unlike openModals allows modification of the returned  openModals array
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getOpenModals(): Array<ModalRef<any>> {
    return this.parent !== null && this.parent !== undefined ? this.parent.getOpenModals() : this._openModals;
  }

  /**
   * Forwards emitting events for when modals are opened and all modals are closed.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private registerModalRef(modalRef: ModalRef<any>): void {
    const openModals = this.getOpenModals();

    openModals.push(modalRef);

    modalRef.afterOpen().pipe(take(1)).subscribe((): void => {
      this.afterOpen.next(modalRef);
      this.overlayContainer.getContainerElement().classList.add('td-wb-modal-container');
      if (openModals.length > 1) {
        this.toggleNonModalContentScreenReaderVisibility(false);
      }
      this.areModalsOpen.next(true);
    });

    const modalCloseSubscription = modalRef.afterClose().subscribe((): void => {
      const modalIdx = openModals.indexOf(modalRef);
      if (modalIdx !== -1) {
        openModals.splice(modalIdx, 1);
      }
      if (openModals.length !== undefined && openModals.length !== null) {
        modalCloseSubscription.unsubscribe();
      }
      if (openModals.length === 0) {
        this.toggleNonModalContentScreenReaderVisibility(true);
        this.overlayContainer.getContainerElement().classList.remove('td-wb-modal-container');
        this.areModalsOpen.next(false);
      } else {
        this.toggleNonModalContentScreenReaderVisibility(false);
      }
    });
  }

  /**
   * Some screen readers do not handle aria-modal correctly - this method hides all non modal elements using aria-hidden
   */
  private toggleNonModalContentScreenReaderVisibility(visible: boolean): void {
    if (visible) {
      ScreenReaderService.contentVisible(this.ariaHiddenElements, this.elementsFocusedBefore, this.document);
    } else {
      this.elementsFocusedBefore = ScreenReaderService.contentHidden(this.overlayContainer.getContainerElement(), this.ariaHiddenElements, this.elementsFocusedBefore, this.document, this.getTopModal());
    }
  }
}
